import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Trash2, AlertTriangle, Package, Calendar, TrendingDown, BarChart3, XCircle, Check, ChefHat } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const WASTE_REASONS = [
  { value: 'spoilage', label: 'Bozulma' },
  { value: 'expiry', label: 'Son Kullanma Tarihi' },
  { value: 'burn', label: 'Yanma' },
  { value: 'spill', label: 'Dökülme' },
  { value: 'breakage', label: 'Kırılma' },
  { value: 'other', label: 'Diğer' },
];

const LOCATIONS = [
  { value: 'warehouse', label: 'Depo' },
  { value: 'prep', label: 'Hazırlık Mutfağı' },
  { value: 'store', label: 'Mağaza/Ocak' },
];

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
};

const formatNumber = (value, decimals = 2) => {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value || 0);
};

export default function WasteTracking() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('new');
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [recipesError, setRecipesError] = useState(null);
  const [recipesLoading, setRecipesLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [productsLoading, setProductsLoading] = useState(true);
  const [wasteRecords, setWasteRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dailySummary, setDailySummary] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);

  // Form state
  const [wasteType, setWasteType] = useState('product'); // 'product' | 'recipe'
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [recipeSearchOpen, setRecipeSearchOpen] = useState(false);
  const [recipeSearchQuery, setRecipeSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    product_id: '',
    recipe_id: '',
    quantity: '',
    location: 'store',
    reason: 'spoilage',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingRecord, setCancellingRecord] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Filter state
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'daily') {
      fetchDailySummary();
    } else if (activeTab === 'monthly') {
      fetchMonthlySummary();
    }
  }, [activeTab, selectedDate, selectedMonth]);

  const errorText = (label, err) => {
    const st = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (err?.code === 'ECONNABORTED') return `${label} yüklenemedi (zaman aşımı)`;
    return `${label} yüklenemedi (${st ? 'HTTP ' + st : err?.message || 'bağlantı hatası'}${typeof detail === 'string' ? ': ' + detail : ''})`;
  };
  const isAuthErr = (err) => [401, 403].includes(err?.response?.status);

  // Her liste bağımsız yüklenir; biri yavaş/başarısız olsa diğerleri gösterilir, mevcut veri silinmez
  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      const res = await api.get('/products', { timeout: 60000 });
      setProducts(res.data || []);
      setProductsError(null);
    } catch (err) {
      if (!isAuthErr(err)) setProductsError(errorText('Ürünler', err));
    } finally {
      setProductsLoading(false);
    }
  };

  const loadRecipes = async () => {
    setRecipesLoading(true);
    try {
      const res = await api.get('/recipes', { timeout: 90000 });
      setRecipes(res.data || []);
      setRecipesError(null);
    } catch (err) {
      if (!isAuthErr(err)) setRecipesError(errorText('Reçeteler', err));
    } finally {
      setRecipesLoading(false);
    }
  };

  const loadRecords = async () => {
    try {
      const res = await api.get('/waste-records?limit=100', { timeout: 30000 });
      setWasteRecords(res.data || []);
    } catch (err) {
      if (!isAuthErr(err)) toast.error(errorText('Zayi kayıtları', err));
    } finally {
      setLoading(false);
    }
  };

  const fetchData = () => {
    loadProducts();
    loadRecipes();
    loadRecords();
  };

  const fetchDailySummary = async () => {
    try {
      const res = await api.get(`/waste-records/summary/daily?date=${selectedDate}`);
      setDailySummary(res.data);
    } catch (error) {
      console.error('Daily summary error:', error);
    }
  };

  const fetchMonthlySummary = async () => {
    try {
      const res = await api.get(`/waste-records/summary/monthly?month=${selectedMonth}`);
      setMonthlySummary(res.data);
    } catch (error) {
      console.error('Monthly summary error:', error);
    }
  };

  const handleCreateWaste = async () => {
    // Validation based on waste type
    if (wasteType === 'product') {
      if (!formData.product_id || !formData.quantity || parseFloat(formData.quantity) <= 0) {
        toast.error('Lütfen ürün ve miktar seçin');
        return;
      }
    } else {
      if (!formData.recipe_id || !formData.quantity || parseInt(formData.quantity) <= 0) {
        toast.error('Lütfen reçete ve porsiyon sayısı seçin');
        return;
      }
    }

    try {
      if (wasteType === 'product') {
        // Ürün zayisi
        const idempotencyKey = `waste_product_${formData.product_id}_${formData.date}_${Date.now()}`;
        
        await api.post('/waste-records', {
          product_id: formData.product_id,
          quantity: parseFloat(formData.quantity),
          location: formData.location,
          reason: formData.reason,
          date: formData.date,
          notes: formData.notes,
          idempotency_key: idempotencyKey,
        });
        
        toast.success('Ürün zayi kaydı oluşturuldu ve stoktan düşüldü');
      } else {
        // Reçete zayisi
        const idempotencyKey = `waste_recipe_${formData.recipe_id}_${formData.date}_${Date.now()}`;
        
        const response = await api.post('/waste-records/recipe', {
          recipe_id: formData.recipe_id,
          quantity: parseInt(formData.quantity),
          location: formData.location,
          reason: formData.reason,
          date: formData.date,
          notes: formData.notes,
          idempotency_key: idempotencyKey,
        });
        
        const record = response.data;
        if (record.stock_movement_type === 'cost_only') {
          toast.success('Reçete zayi kaydı oluşturuldu (Sadece maliyet kaydı - stok değişmedi)');
        } else {
          toast.success('Reçete zayi kaydı oluşturuldu ve mamul stoğundan düşüldü');
        }
      }
      
      setFormData({
        product_id: '',
        recipe_id: '',
        quantity: '',
        location: 'store',
        reason: 'spoilage',
        date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Zayi kaydı oluşturulamadı');
    }
  };

  const handleCancelWaste = async () => {
    if (!cancelReason.trim()) {
      toast.error('İptal nedeni zorunludur');
      return;
    }

    try {
      await api.post(`/waste-records/${cancellingRecord.id}/cancel`, {
        cancel_reason: cancelReason,
      });
      
      // Reçete zayisinde stok değişmemişse farklı mesaj
      if (cancellingRecord.waste_type === 'recipe' && cancellingRecord.stock_movement_type === 'cost_only') {
        toast.success('Reçete zayi kaydı iptal edildi');
      } else {
        toast.success('Zayi kaydı iptal edildi ve stok geri yüklendi');
      }
      
      setCancelDialogOpen(false);
      setCancellingRecord(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İptal işlemi başarısız');
    }
  };

  const selectedProduct = products.find(p => p.id === formData.product_id);
  const selectedRecipe = recipes.find(r => r.id === formData.recipe_id);
  
  const estimatedCost = wasteType === 'product'
    ? (selectedProduct && formData.quantity 
        ? (selectedProduct.unit_price || 0) * parseFloat(formData.quantity || 0)
        : 0)
    : (selectedRecipe && formData.quantity
        ? (selectedRecipe.cost_per_portion || 0) * parseInt(formData.quantity || 0)
        : 0);

  // Summary stats
  const activeRecords = wasteRecords.filter(r => r.status === 'active');
  const todayRecords = activeRecords.filter(r => r.date === new Date().toISOString().split('T')[0]);
  const totalCost = activeRecords.reduce((sum, r) => sum + r.total_cost, 0);
  const todayCost = todayRecords.reduce((sum, r) => sum + r.total_cost, 0);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Zayi / Fire Takibi</h1>
            <p className="text-muted-foreground">Ürün kayıplarını kaydedin ve analiz edin</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Bugünkü Zayi</p>
                  <p className="text-2xl font-bold">{todayRecords.length} kayıt</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <TrendingDown className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Bugünkü Maliyet</p>
                  <p className="text-2xl font-bold">{formatCurrency(todayCost)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Package className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Toplam Kayıt</p>
                  <p className="text-2xl font-bold">{activeRecords.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Toplam Maliyet</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalCost)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="new" data-testid="new-waste-tab">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Kayıt
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Geçmiş
            </TabsTrigger>
            <TabsTrigger value="daily" data-testid="daily-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Günlük Rapor
            </TabsTrigger>
            <TabsTrigger value="monthly" data-testid="monthly-tab">
              <TrendingDown className="w-4 h-4 mr-2" />
              Aylık Rapor
            </TabsTrigger>
          </TabsList>

          {/* New Waste Record Tab */}
          <TabsContent value="new" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Yeni Zayi Kaydı</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Waste Type Selection */}
                <div className="space-y-3">
                  <Label>Zayi Türü *</Label>
                  <RadioGroup 
                    value={wasteType} 
                    onValueChange={(value) => {
                      setWasteType(value);
                      setFormData({
                        ...formData,
                        product_id: '',
                        recipe_id: '',
                        quantity: '',
                      });
                    }}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="product" id="waste-product" data-testid="waste-type-product" />
                      <Label htmlFor="waste-product" className="flex items-center gap-2 cursor-pointer">
                        <Package className="w-4 h-4" />
                        Ürün Zayisi
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="recipe" id="waste-recipe" data-testid="waste-type-recipe" />
                      <Label htmlFor="waste-recipe" className="flex items-center gap-2 cursor-pointer">
                        <ChefHat className="w-4 h-4" />
                        Reçete / Hazır Yemek Zayisi
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Product Search (Ürün Zayisi için) */}
                  {wasteType === 'product' && (
                    <div className="space-y-2">
                      <Label>Ürün *</Label>
                      <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={productSearchOpen}
                            className="w-full justify-between font-normal h-10"
                            data-testid="waste-product-select"
                          >
                            {formData.product_id ? (
                              <span className="truncate">
                                {products.find(p => p.id === formData.product_id)?.name || 'Ürün seçildi'}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Ürün ara...</span>
                            )}
                            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0 z-[100]" align="start">
                          <Command
                            filter={(value, search) => {
                              if (!search) return 1;
                              const normalize = (str) => str
                                .toLocaleLowerCase('tr-TR')
                                .replace(/ı/g, 'i')
                                .replace(/ğ/g, 'g')
                                .replace(/ü/g, 'u')
                                .replace(/ş/g, 's')
                                .replace(/ö/g, 'o')
                                .replace(/ç/g, 'c')
                                .replace(/İ/g, 'i');
                              
                              const normalizedValue = normalize(value);
                              const normalizedSearch = normalize(search);
                              
                              if (normalizedValue.includes(normalizedSearch)) return 1;
                              return 0;
                            }}
                          >
                            <CommandInput 
                              placeholder="Ürün ara..." 
                              value={productSearchQuery}
                              onValueChange={setProductSearchQuery}
                            />
                            <CommandList className="max-h-[300px]">
                              {productsError ? (
                                <div className="p-3 text-sm text-destructive space-y-2" data-testid="waste-products-error">
                                  <p>{productsError}</p>
                                  <Button type="button" size="sm" variant="outline" onClick={loadProducts} data-testid="waste-products-retry">
                                    Yeniden Dene
                                  </Button>
                                </div>
                              ) : productsLoading && products.length === 0 ? (
                                <div className="p-3 text-sm text-muted-foreground" data-testid="waste-products-loading">Ürünler yükleniyor...</div>
                              ) : (
                                <CommandEmpty>Sonuç bulunamadı</CommandEmpty>
                              )}
                              <CommandGroup heading={`Ürünler (${products.length})`}>
                                {products.map((product) => (
                                  <CommandItem
                                    key={product.id}
                                    value={product.name}
                                    onSelect={() => {
                                      setFormData({ ...formData, product_id: product.id });
                                      setProductSearchQuery('');
                                      setProductSearchOpen(false);
                                    }}
                                    className="cursor-pointer"
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <div className="flex items-center gap-2">
                                        <span className="truncate">{product.name}</span>
                                        <span className="text-xs text-muted-foreground">({product.unit})</span>
                                      </div>
                                      <span className="text-xs font-medium">
                                        {formatCurrency(product.unit_price)}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}

                  {/* Recipe Search (Reçete Zayisi için) */}
                  {wasteType === 'recipe' && (
                    <div className="space-y-2">
                      <Label>Reçete *</Label>
                      <Popover open={recipeSearchOpen} onOpenChange={setRecipeSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={recipeSearchOpen}
                            className="w-full justify-between font-normal h-10"
                            data-testid="waste-recipe-select"
                          >
                            {formData.recipe_id ? (
                              <span className="truncate">
                                {recipes.find(r => r.id === formData.recipe_id)?.name || 'Reçete seçildi'}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Reçete ara...</span>
                            )}
                            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0 z-[100]" align="start">
                          <Command
                            filter={(value, search) => {
                              if (!search) return 1;
                              const normalize = (str) => str
                                .toLocaleLowerCase('tr-TR')
                                .replace(/ı/g, 'i')
                                .replace(/ğ/g, 'g')
                                .replace(/ü/g, 'u')
                                .replace(/ş/g, 's')
                                .replace(/ö/g, 'o')
                                .replace(/ç/g, 'c')
                                .replace(/İ/g, 'i');
                              
                              const normalizedValue = normalize(value);
                              const normalizedSearch = normalize(search);
                              
                              if (normalizedValue.includes(normalizedSearch)) return 1;
                              return 0;
                            }}
                          >
                            <CommandInput 
                              placeholder="Reçete ara..." 
                              value={recipeSearchQuery}
                              onValueChange={setRecipeSearchQuery}
                            />
                            <CommandList className="max-h-[300px]">
                              {recipesError ? (
                                <div className="p-3 text-sm text-destructive space-y-2" data-testid="waste-recipes-error">
                                  <p>{recipesError}</p>
                                  <Button type="button" size="sm" variant="outline" onClick={loadRecipes} data-testid="waste-recipes-retry">
                                    Yeniden Dene
                                  </Button>
                                </div>
                              ) : recipesLoading && recipes.length === 0 ? (
                                <div className="p-3 text-sm text-muted-foreground" data-testid="waste-recipes-loading">Reçeteler yükleniyor...</div>
                              ) : (
                                <CommandEmpty>Sonuç bulunamadı</CommandEmpty>
                              )}
                              {!recipesError && !(recipesLoading && recipes.length === 0) && (
                              <CommandGroup heading={`Reçeteler (${recipes.length})`}>
                                {recipes.map((recipe) => (
                                  <CommandItem
                                    key={recipe.id}
                                    value={recipe.name}
                                    onSelect={() => {
                                      setFormData({ ...formData, recipe_id: recipe.id });
                                      setRecipeSearchQuery('');
                                      setRecipeSearchOpen(false);
                                    }}
                                    className="cursor-pointer"
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <div className="flex items-center gap-2">
                                        <ChefHat className="w-4 h-4 text-orange-500" />
                                        <span className="truncate">{recipe.name}</span>
                                        <Badge variant="outline" className="text-xs">
                                          {recipe.recipe_type === 'portion_transfer' ? 'Mamul' : 'Maliyet'}
                                        </Badge>
                                      </div>
                                      <span className="text-xs font-medium">
                                        {formatCurrency(recipe.cost_per_portion || 0)}/porsiyon
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {selectedRecipe && (
                        <div className="text-xs text-muted-foreground mt-1 space-y-1">
                          <p>
                            <strong>Tip:</strong> {selectedRecipe.recipe_type === 'portion_transfer' 
                              ? 'Mamul Üretim (stoktan düşülür)' 
                              : 'Sadece Maliyet (stok değişmez)'}
                          </p>
                          <p>
                            <strong>Porsiyon Maliyeti:</strong> {formatCurrency(selectedRecipe.cost_per_portion || 0)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quantity */}
                  <div className="space-y-2">
                    <Label>{wasteType === 'recipe' ? 'Porsiyon Sayısı *' : 'Miktar *'}</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        step={wasteType === 'recipe' ? '1' : '0.01'}
                        min={wasteType === 'recipe' ? '1' : '0'}
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        placeholder={wasteType === 'recipe' ? '1' : '0.00'}
                        data-testid="waste-quantity-input"
                      />
                      {wasteType === 'product' && selectedProduct && (
                        <span className="flex items-center text-sm text-muted-foreground">
                          {selectedProduct.unit}
                        </span>
                      )}
                      {wasteType === 'recipe' && (
                        <span className="flex items-center text-sm text-muted-foreground">
                          porsiyon
                        </span>
                      )}
                    </div>
                    {wasteType === 'product' && selectedProduct && (
                      <p className="text-xs text-muted-foreground">
                        Mevcut stoklar - Depo: {formatNumber(selectedProduct.warehouse_stock || 0)}, 
                        Hazırlık: {formatNumber(selectedProduct.prep_stock || 0)}, 
                        Mağaza: {formatNumber(selectedProduct.store_stock || 0)}
                      </p>
                    )}
                  </div>

                  {/* Location */}
                  <div className="space-y-2">
                    <Label>Stok Konumu *</Label>
                    <Select
                      value={formData.location}
                      onValueChange={(value) => setFormData({ ...formData, location: value })}
                    >
                      <SelectTrigger data-testid="waste-location-select">
                        <SelectValue placeholder="Konum seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {LOCATIONS.map((loc) => (
                          <SelectItem key={loc.value} value={loc.value}>
                            {loc.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Reason */}
                  <div className="space-y-2">
                    <Label>Zayi Nedeni *</Label>
                    <Select
                      value={formData.reason}
                      onValueChange={(value) => setFormData({ ...formData, reason: value })}
                    >
                      <SelectTrigger data-testid="waste-reason-select">
                        <SelectValue placeholder="Neden seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {WASTE_REASONS.map((reason) => (
                          <SelectItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date */}
                  <div className="space-y-2">
                    <Label>Tarih *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      data-testid="waste-date-input"
                    />
                  </div>

                  {/* Estimated Cost */}
                  <div className="space-y-2">
                    <Label>Tahmini Maliyet</Label>
                    <div className="h-10 flex items-center px-3 bg-muted rounded-md">
                      <span className="text-lg font-semibold text-red-600">
                        {formatCurrency(estimatedCost)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Zayi ile ilgili notlar..."
                    rows={3}
                    data-testid="waste-notes-input"
                  />
                </div>

                <Button 
                  onClick={handleCreateWaste} 
                  className="w-full bg-red-600 hover:bg-red-700"
                  data-testid="create-waste-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Zayi Kaydı Oluştur
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Zayi Geçmişi</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Ürün / Reçete</TableHead>
                        <TableHead>Miktar</TableHead>
                        <TableHead>Konum</TableHead>
                        <TableHead>Neden</TableHead>
                        <TableHead className="text-right">Maliyet</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Stok</TableHead>
                        <TableHead>Kaydeden</TableHead>
                        {isAdmin && <TableHead></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {wasteRecords.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                            Henüz zayi kaydı yok
                          </TableCell>
                        </TableRow>
                      ) : (
                        wasteRecords.map((record) => (
                          <TableRow 
                            key={record.id}
                            className={record.status === 'cancelled' ? 'opacity-50' : ''}
                          >
                            <TableCell>{record.date}</TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {record.waste_type === 'recipe' ? (
                                  <ChefHat className="w-4 h-4 text-orange-500" />
                                ) : (
                                  <Package className="w-4 h-4 text-blue-500" />
                                )}
                                {record.waste_type === 'recipe' ? record.recipe_name : record.product_name}
                              </div>
                            </TableCell>
                            <TableCell>
                              {formatNumber(record.quantity)} {record.unit}
                            </TableCell>
                            <TableCell>{record.location_label}</TableCell>
                            <TableCell>{record.reason_label}</TableCell>
                            <TableCell className="text-right font-medium text-red-600">
                              {formatCurrency(record.total_cost)}
                            </TableCell>
                            <TableCell>
                              {record.status === 'active' ? (
                                <Badge variant="outline" className="bg-green-100 text-green-700">
                                  <Check className="w-3 h-3 mr-1" />
                                  Aktif
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-gray-100 text-gray-700">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  İptal
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {record.stock_movement_type === 'cost_only' ? (
                                <Badge variant="secondary" className="text-xs">
                                  Maliyet
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs bg-blue-50">
                                  Stoktan
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {record.created_by_name}
                            </TableCell>
                            {isAdmin && (
                              <TableCell>
                                {record.status === 'active' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setCancellingRecord(record);
                                      setCancelDialogOpen(true);
                                    }}
                                    className="text-red-600 hover:text-red-700"
                                    data-testid={`cancel-waste-${record.id}`}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Daily Report Tab */}
          <TabsContent value="daily">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Günlük Zayi Raporu</CardTitle>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-48"
                />
              </CardHeader>
              <CardContent>
                {dailySummary ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <p className="text-sm text-muted-foreground">Kayıt Sayısı</p>
                          <p className="text-3xl font-bold">{dailySummary.total_records}</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <p className="text-sm text-muted-foreground">Toplam Miktar</p>
                          <p className="text-3xl font-bold">{formatNumber(dailySummary.total_quantity)}</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <p className="text-sm text-muted-foreground">Toplam Maliyet</p>
                          <p className="text-3xl font-bold text-red-600">{formatCurrency(dailySummary.total_cost)}</p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      {/* By Location Chart */}
                      <div>
                        <h3 className="text-sm font-semibold mb-4">Konuma Göre Dağılım</h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={Object.entries(dailySummary.by_location).map(([key, val], i) => ({
                                name: val.label,
                                value: val.cost,
                              }))}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {Object.keys(dailySummary.by_location).map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      {/* By Reason Chart */}
                      <div>
                        <h3 className="text-sm font-semibold mb-4">Nedene Göre Dağılım</h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={Object.entries(dailySummary.by_reason).map(([key, val], i) => ({
                                name: val.label,
                                value: val.cost,
                              }))}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {Object.keys(dailySummary.by_reason).map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Bu tarihte zayi kaydı yok</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Report Tab */}
          <TabsContent value="monthly">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Aylık Zayi Raporu</CardTitle>
                <Input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-48"
                />
              </CardHeader>
              <CardContent>
                {monthlySummary ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <p className="text-sm text-muted-foreground">Toplam Kayıt</p>
                          <p className="text-3xl font-bold">{monthlySummary.total_records}</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <p className="text-sm text-muted-foreground">Toplam Maliyet</p>
                          <p className="text-3xl font-bold text-red-600">{formatCurrency(monthlySummary.total_cost)}</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Daily Breakdown Chart */}
                    <div>
                      <h3 className="text-sm font-semibold mb-4">Günlük Dağılım</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={monthlySummary.daily_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tickFormatter={(d) => d.split('-')[2]} />
                          <YAxis />
                          <Tooltip 
                            formatter={(value) => formatCurrency(value)}
                            labelFormatter={(label) => `Tarih: ${label}`}
                          />
                          <Bar dataKey="cost" fill="#ef4444" name="Maliyet" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Top Products */}
                    <div>
                      <h3 className="text-sm font-semibold mb-4">En Çok Zayi Edilen Ürünler</h3>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Ürün</TableHead>
                              <TableHead className="text-right">Toplam Miktar</TableHead>
                              <TableHead className="text-right">Kayıt Sayısı</TableHead>
                              <TableHead className="text-right">Toplam Maliyet</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {monthlySummary.top_products.map((p, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-medium">{p.product_name}</TableCell>
                                <TableCell className="text-right">{formatNumber(p.total_quantity)} {p.unit}</TableCell>
                                <TableCell className="text-right">{p.record_count}</TableCell>
                                <TableCell className="text-right font-medium text-red-600">
                                  {formatCurrency(p.total_cost)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Bu ayda zayi kaydı yok</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Cancel Dialog */}
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Zayi Kaydını İptal Et</AlertDialogTitle>
              <AlertDialogDescription>
                Bu zayi kaydını iptal etmek istediğinizden emin misiniz? 
                Stok miktarı geri yüklenecek ve kayıt iptal olarak işaretlenecektir.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {cancellingRecord && (
              <div className="py-4 space-y-2 text-sm">
                <p><strong>Ürün:</strong> {cancellingRecord.product_name}</p>
                <p><strong>Miktar:</strong> {formatNumber(cancellingRecord.quantity)} {cancellingRecord.unit}</p>
                <p><strong>Maliyet:</strong> {formatCurrency(cancellingRecord.total_cost)}</p>
                <div className="pt-2">
                  <Label>İptal Nedeni *</Label>
                  <Textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="İptal nedenini açıklayın..."
                    className="mt-2"
                    data-testid="cancel-reason-input"
                  />
                </div>
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setCancelReason('');
                setCancellingRecord(null);
              }}>
                Vazgeç
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelWaste}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-cancel-btn"
              >
                İptal Et
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
